import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import {
  FiUser,
  FiPhone,
  FiCreditCard,
  FiBriefcase,
  FiCheckCircle,
  FiArrowLeft,
  FiArrowRight,
  FiSave,
  FiPrinter,
  FiDownload,
  FiAlertTriangle,
  FiUserPlus,
  FiClipboard,
  FiUpload,
  FiX,
  FiImage,
  FiFileText,
} from 'react-icons/fi';
import { registrationService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { exportPDF } from '../../utils/exporters';

const STEPS = [
  { id: 1, label: 'Personal', icon: <FiUser size={18} /> },
  { id: 2, label: 'Contact', icon: <FiPhone size={18} /> },
  { id: 3, label: 'Documents', icon: <FiCreditCard size={18} /> },
  { id: 4, label: 'Business', icon: <FiBriefcase size={18} /> },
  { id: 5, label: 'Preview', icon: <FiAlertTriangle size={18} /> },
  { id: 6, label: 'Success', icon: <FiCheckCircle size={18} /> },
];

const INITIAL_FORM = {
  title: 'Mr',
  first_name: '',
  middle_name: '',
  last_name: '',
  date_of_birth: '',
  place_of_birth: '',
  sex: 'Male',
  nationality: 'LR',
  marital_status: '',
  employment_status: '',
  number_of_children: 0,
  email: '',
  phone: '',
  address: '',
  postal_address: '',
  county: '',
  district: '',
  township: '',
  community: '',
  postal_code: '',
  passport_number: '',
  birth_certificate_number: '',
  driver_license_number: '',
  national_id_number: '',
  business_name: '',
  business_type: '',
  business_sector: '',
  business_location: '',
  tax_division: 'small_micro',
  notes: '',
};

const NewRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [files, setFiles] = useState({
    user_photo: null,
    passport: null,
    birth_certificate: null,
    drivers_license: null,
    national_id: null,
  });

  const { data: referenceData, isLoading: loadingRef } = useQuery(
    'reg-reference',
    registrationService.getReference,
    { staleTime: 60 * 60 * 1000 }
  );

  const reference = referenceData?.data || {};
  const postalCodes = reference.postal_codes || {};

  const previewMutation = useMutation(registrationService.preview, {
    onSuccess: (data) => {
      if (data.success) {
        setPreview(data.data);
        setStep(5);
      } else {
        toast.error(data.message || 'Preview failed');
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.message || 'Preview failed');
    },
  });

  const registerMutation = useMutation(registrationService.register, {
    onSuccess: (data) => {
      if (data.success) {
        setResult(data.data);
        setStep(6);
        toast.success('Taxpayer registered successfully');
      } else {
        toast.error(data.message || 'Registration failed');
      }
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message || 'Registration failed';
      toast.error(msg);
      const dupes = err.response?.data?.duplicate_warnings;
      if (dupes && dupes.length > 0) {
        toast.error(dupes[0].message, { duration: 6000 });
      }
    },
  });

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateFile = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
  };

  // Cascading helpers
  const districts = form.county && postalCodes[form.county]
    ? Array.from(new Set(postalCodes[form.county].map((r) => r.district))).sort()
    : [];

  const townships = form.county && form.district && postalCodes[form.county]
    ? Array.from(
        new Set(
          postalCodes[form.county]
            .filter((r) => r.district === form.district)
            .map((r) => r.area)
        )
      ).sort()
    : [];

  const communities = form.county && form.district && form.township && postalCodes[form.county]
    ? postalCodes[form.county]
        .filter((r) => r.district === form.district && r.area === form.township)
        .map((r) => r.community)
        .sort()
    : [];

  const handleCountyChange = (county) => {
    setForm((prev) => ({
      ...prev,
      county,
      district: '',
      township: '',
      community: '',
      postal_code: '',
    }));
  };

  const handleDistrictChange = (district) => {
    setForm((prev) => ({
      ...prev,
      district,
      township: '',
      community: '',
      postal_code: '',
    }));
  };

  const handleTownshipChange = (township) => {
    setForm((prev) => ({
      ...prev,
      township,
      community: '',
      postal_code: '',
    }));
  };

  const handleCommunityChange = (community) => {
    const row = postalCodes[form.county]?.find(
      (r) =>
        r.district === form.district &&
        r.area === form.township &&
        r.community === community
    );
    setForm((prev) => ({
      ...prev,
      community,
      postal_code: row ? row.code : '',
    }));
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    if (step === 4) {
      previewMutation.mutate(form);
      return;
    }
    setStep((s) => Math.min(s + 1, 6));
  };

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleRegister = async () => {
    setUploading(true);
    try {
      // Phase 1: upload all files in parallel
      const uploadPromises = Object.entries(files)
        .filter(([_, file]) => file !== null)
        .map(async ([key, file]) => {
          const response = await registrationService.uploadFile(file);
          if (!response.success) throw new Error(`Upload failed for ${key}`);
          return { key, url: response.data.url };
        });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Phase 2: build URL map
      const fileUrls = {
        user_photo_url: null,
        passport_image_url: null,
        birth_certificate_image_url: null,
        drivers_license_image_url: null,
        national_id_image_url: null,
      };

      uploadedFiles.forEach(({ key, url }) => {
        if (key === 'user_photo') fileUrls.user_photo_url = url;
        if (key === 'passport') fileUrls.passport_image_url = url;
        if (key === 'birth_certificate') fileUrls.birth_certificate_image_url = url;
        if (key === 'drivers_license') fileUrls.drivers_license_image_url = url;
        if (key === 'national_id') fileUrls.national_id_image_url = url;
      });

      // Phase 3: register
      const payload = {
        ...form,
        address:
          form.address ||
          [form.community, form.township, form.district, form.county]
            .filter(Boolean)
            .join(', '),
        postal_address: form.postal_code || form.postal_address,
        ...fileUrls,
      };

      registerMutation.mutate(payload);
    } catch (err) {
      console.error('Upload phase failed:', err);
      toast.error(
        'File upload failed. Registration was NOT saved. Please try again.',
        { duration: 6000 }
      );
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setFiles({
      user_photo: null,
      passport: null,
      birth_certificate: null,
      drivers_license: null,
      national_id: null,
    });
    setPreview(null);
    setResult(null);
    setStep(1);
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (!form.first_name) return fail('First name is required');
      if (!form.last_name) return fail('Last name is required');
      if (!form.date_of_birth) return fail('Date of birth is required');
    }
    if (s === 2) {
      if (!form.email) return fail('Email is required');
      if (!form.phone) return fail('Phone is required');
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(form.email)) return fail('Invalid email format');
      if (!form.county) return fail('County is required');
      if (!form.district) return fail('District is required');
    }
    if (s === 4) {
      if (!form.business_name) return fail('Business name is required');
      if (!form.business_type) return fail('Business type is required');
      if (!form.business_sector) return fail('Business sector is required');
    }
    return true;
  };

  const fail = (msg) => {
    toast.error(msg);
    return false;
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = () => {
    if (!result) return;
    try {
      exportPDF({
        title: 'Taxpayer Registration Certificate',
        subtitle: `Registration ID: ${result.registration_id}`,
        summaryRows: [
          { label: 'Full Name', value: `${result.first_name} ${result.last_name}` },
          { label: 'DSSN', value: result.dssn },
          { label: 'TIN', value: result.tin },
          { label: 'Business Name', value: result.business_name },
          { label: 'Business ID', value: result.business_id },
          { label: 'Tax Division', value: result.tax_division },
          { label: 'Email (login)', value: result.email },
          { label: 'Phone', value: result.phone },
          { label: 'Temporary Password', value: result.temporary_password },
        ],
        columns: [
          { key: 'label', label: 'Field' },
          { key: 'value', label: 'Value' },
        ],
        rows: [
          { label: 'Registration ID', value: result.registration_id },
          { label: 'DSSN (login)', value: result.dssn },
          { label: 'TIN', value: result.tin },
          { label: 'Email (login)', value: result.email },
          { label: 'Temporary Password', value: '12345' },
        ],
        filename: `LRA-registration-${result.dssn}.pdf`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PDF');
    }
  };

  if (loadingRef) return <LoadingSpinner message="Loading form..." />;

  return (
    <div className="registration-wizard">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="icon-btn" onClick={() => navigate('/lra/intelligence')}>
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">Register New Taxpayer</h1>
            <p className="page-subtitle">
              Collect identity and business information to issue a TIN
            </p>
          </div>
        </div>
      </div>

      <div className="steps-indicator">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={`step ${step === s.id ? 'active' : ''} ${step > s.id ? 'done' : ''}`}
          >
            <div className="step-icon">{s.icon}</div>
            <div className="step-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {step === 1 && <PersonalStep form={form} update={updateForm} reference={reference} />}
        {step === 2 && (
          <ContactStep
            form={form}
            update={updateForm}
            reference={reference}
            districts={districts}
            townships={townships}
            communities={communities}
            onCountyChange={handleCountyChange}
            onDistrictChange={handleDistrictChange}
            onTownshipChange={handleTownshipChange}
            onCommunityChange={handleCommunityChange}
            hasPostalData={!!postalCodes[form.county]}
          />
        )}
        {step === 3 && (
          <DocumentsStep form={form} update={updateForm} files={files} onFileChange={updateFile} />
        )}
        {step === 4 && <BusinessStep form={form} update={updateForm} reference={reference} />}
        {step === 5 && preview && <PreviewStep preview={preview} form={form} files={files} />}
        {step === 6 && result && (
          <SuccessStep
            result={result}
            onPrint={handlePrint}
            onDownloadPDF={handleDownloadPDF}
            onAnother={handleReset}
            onHistory={() => navigate('/lra/registration/history')}
          />
        )}

        {step < 6 && (
          <div className="form-actions" style={{ marginTop: 24 }}>
            {step > 1 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
                disabled={uploading || registerMutation.isLoading}
              >
                <FiArrowLeft size={18} />
                Back
              </button>
            )}
            <div style={{ flex: 1 }} />
            {step === 5 ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRegister}
                disabled={uploading || registerMutation.isLoading}
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
              >
                <FiSave size={18} />
                {uploading
                  ? 'Uploading files...'
                  : registerMutation.isLoading
                  ? 'Registering...'
                  : 'Confirm & Register'}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={previewMutation.isLoading}
              >
                {previewMutation.isLoading
                  ? 'Generating preview...'
                  : step === 4
                  ? 'Preview Credentials'
                  : 'Next'}
                <FiArrowRight size={18} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================
// STEP 1 — Personal
// =============================================
const PersonalStep = ({ form, update, reference }) => (
  <>
    <h2 className="section-title">Personal Information</h2>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Title</label>
        <select className="form-select" value={form.title} onChange={(e) => update('title', e.target.value)}>
          <option>Mr</option>
          <option>Mrs</option>
          <option>Ms</option>
          <option>Dr</option>
          <option>Chief</option>
          <option>Hon</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Sex</label>
        <select className="form-select" value={form.sex} onChange={(e) => update('sex', e.target.value)}>
          <option>Male</option>
          <option>Female</option>
          <option>Other</option>
        </select>
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">First Name *</label>
        <input type="text" className="form-input" value={form.first_name} onChange={(e) => update('first_name', e.target.value)} placeholder="e.g., Yassah" />
      </div>
      <div className="form-group">
        <label className="form-label">Middle Name</label>
        <input type="text" className="form-input" value={form.middle_name} onChange={(e) => update('middle_name', e.target.value)} placeholder="Optional" />
      </div>
      <div className="form-group">
        <label className="form-label">Last Name *</label>
        <input type="text" className="form-input" value={form.last_name} onChange={(e) => update('last_name', e.target.value)} placeholder="e.g., Paygar" />
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Date of Birth *</label>
        <input type="date" className="form-input" value={form.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)} max={new Date().toISOString().split('T')[0]} />
      </div>
      <div className="form-group">
        <label className="form-label">Place of Birth</label>
        <input type="text" className="form-input" value={form.place_of_birth} onChange={(e) => update('place_of_birth', e.target.value)} placeholder="e.g., Monrovia" />
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Nationality</label>
        <select className="form-select" value={form.nationality} onChange={(e) => update('nationality', e.target.value)}>
          <option value="LR">Liberian</option>
          <option value="US">American</option>
          <option value="GB">British</option>
          <option value="NG">Nigerian</option>
          <option value="GH">Ghanaian</option>
          <option value="SL">Sierra Leonean</option>
          <option value="GN">Guinean</option>
          <option value="CI">Ivorian</option>
          <option value="OT">Other</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Marital Status</label>
        <select className="form-select" value={form.marital_status} onChange={(e) => update('marital_status', e.target.value)}>
          <option value="">Select...</option>
          {(reference.marital_statuses || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Employment Status</label>
        <select className="form-select" value={form.employment_status} onChange={(e) => update('employment_status', e.target.value)}>
          <option value="">Select...</option>
          {(reference.employment_statuses || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Number of Children</label>
        <input type="number" min="0" className="form-input" value={form.number_of_children} onChange={(e) => update('number_of_children', parseInt(e.target.value) || 0)} />
      </div>
    </div>
  </>
);

// =============================================
// STEP 2 — Contact with cascading postal codes
// =============================================
const ContactStep = ({
  form, update, reference, districts, townships, communities,
  onCountyChange, onDistrictChange, onTownshipChange, onCommunityChange, hasPostalData,
}) => (
  <>
    <h2 className="section-title">Contact Information</h2>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Email (login) *</label>
        <input type="email" className="form-input" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="e.g., user@example.com" />
      </div>
      <div className="form-group">
        <label className="form-label">Phone *</label>
        <input type="text" className="form-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="e.g., +231770000000" />
      </div>
    </div>

    <h3 style={{ fontSize: 15, fontWeight: 600, marginTop: 24, marginBottom: 12, color: '#374151' }}>Location</h3>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">County *</label>
        <select className="form-select" value={form.county} onChange={(e) => onCountyChange(e.target.value)}>
          <option value="">Select...</option>
          {(reference.counties || []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">District *</label>
        <select className="form-select" value={form.district} onChange={(e) => onDistrictChange(e.target.value)} disabled={!form.county || districts.length === 0}>
          <option value="">Select...</option>
          {districts.map((d) => (<option key={d} value={d}>{d}</option>))}
        </select>
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Township</label>
        <select className="form-select" value={form.township} onChange={(e) => onTownshipChange(e.target.value)} disabled={!form.district || townships.length === 0}>
          <option value="">Select...</option>
          {townships.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Community</label>
        <select className="form-select" value={form.community} onChange={(e) => onCommunityChange(e.target.value)} disabled={!form.township || communities.length === 0}>
          <option value="">Select...</option>
          {communities.map((c) => (<option key={c} value={c}>{c}</option>))}
        </select>
      </div>
    </div>

    {!hasPostalData && form.county && (
      <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e', marginBottom: 20 }}>
        Postal code data is not yet available for <strong>{form.county}</strong>. You can enter the address manually below.
      </div>
    )}

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Postal Code</label>
        <input type="text" className="form-input" value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)} placeholder="Auto-filled from community" readOnly={!!form.community} style={{ background: form.community ? '#f3f4f6' : 'white' }} />
      </div>
      <div className="form-group">
        <label className="form-label">Residential Address</label>
        <input type="text" className="form-input" value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="e.g., Paynesville, Red Light" />
      </div>
    </div>
  </>
);

// =============================================
// STEP 3 — Documents with uploads
// =============================================
const DocumentsStep = ({ form, update, files, onFileChange }) => (
  <>
    <h2 className="section-title">Identity Documents</h2>
    <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
      All fields below are optional. Max 10 MB per file. Any file type accepted.
    </p>

    <FileInput label="Profile Photo" hint="JPG, PNG, or any image of the taxpayer" file={files.user_photo} onChange={(f) => onFileChange('user_photo', f)} onClear={() => onFileChange('user_photo', null)} />

    <div className="form-group">
      <label className="form-label">Passport Number</label>
      <input type="text" className="form-input" value={form.passport_number} onChange={(e) => update('passport_number', e.target.value)} placeholder="e.g., LBR1234567" />
    </div>
    <FileInput label="Passport Scan" hint="Photo or PDF of the passport page" file={files.passport} onChange={(f) => onFileChange('passport', f)} onClear={() => onFileChange('passport', null)} />

    <div className="form-group">
      <label className="form-label">Birth Certificate Number</label>
      <input type="text" className="form-input" value={form.birth_certificate_number} onChange={(e) => update('birth_certificate_number', e.target.value)} placeholder="Optional" />
    </div>
    <FileInput label="Birth Certificate Scan" hint="Photo or PDF" file={files.birth_certificate} onChange={(f) => onFileChange('birth_certificate', f)} onClear={() => onFileChange('birth_certificate', null)} />

    <div className="form-group">
      <label className="form-label">Driver License Number</label>
      <input type="text" className="form-input" value={form.driver_license_number} onChange={(e) => update('driver_license_number', e.target.value)} placeholder="Optional" />
    </div>
    <FileInput label="Driver License Scan" hint="Photo or PDF" file={files.drivers_license} onChange={(f) => onFileChange('drivers_license', f)} onClear={() => onFileChange('drivers_license', null)} />

    <div className="form-group">
      <label className="form-label">National ID Number</label>
      <input type="text" className="form-input" value={form.national_id_number} onChange={(e) => update('national_id_number', e.target.value)} placeholder="Optional" />
    </div>
    <FileInput label="National ID Scan" hint="Photo or PDF" file={files.national_id} onChange={(f) => onFileChange('national_id', f)} onClear={() => onFileChange('national_id', null)} />
  </>
);

// =============================================
// Reusable File Input
// =============================================
const FileInput = ({ label, hint, file, onChange, onClear }) => {
  const handleChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const sizeMB = selected.size / (1024 * 1024);
    if (sizeMB > 10) {
      toast.error(`File too large. Max 10 MB (selected: ${sizeMB.toFixed(1)} MB)`);
      return;
    }
    onChange(selected);
  };

  const isImage = file && file.type && file.type.startsWith('image/');

  return (
    <div
      className="form-group"
      style={{
        background: file ? '#f0fdf4' : '#f9fafb',
        border: `1px dashed ${file ? '#22c55e' : '#d1d5db'}`,
        borderRadius: 8,
        padding: 16,
      }}
    >
      <label className="form-label" style={{ marginBottom: 8 }}>{label}</label>
      {file ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {isImage ? (
            <img
              src={URL.createObjectURL(file)}
              alt={label}
              style={{
                width: 64,
                height: 64,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #d1d5db',
              }}
            />
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 8,
                background: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
              }}
            >
              <FiFileText size={28} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.name}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              {(file.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClear}
            title="Remove"
            style={{ color: '#dc2626' }}
          >
            <FiX size={18} />
          </button>
        </div>
      ) : (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            padding: '8px 12px',
            borderRadius: 6,
            background: '#ffffff',
            border: '1px solid #d1d5db',
          }}
        >
          <FiUpload size={18} color="#6b7280" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Choose file</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{hint}</div>
          </div>
          <input
            type="file"
            style={{ display: 'none' }}
            onChange={handleChange}
            accept="*/*"
          />
        </label>
      )}
    </div>
  );
};

// =============================================
// STEP 4 — Business
// =============================================
const BusinessStep = ({ form, update, reference }) => (
  <>
    <h2 className="section-title">Business Information</h2>

    <div className="form-group">
      <label className="form-label">Business Name *</label>
      <input type="text" className="form-input" value={form.business_name} onChange={(e) => update('business_name', e.target.value)} placeholder="e.g., Yassah Paygar Provisions" />
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Business Type *</label>
        <select className="form-select" value={form.business_type} onChange={(e) => update('business_type', e.target.value)}>
          <option value="">Select...</option>
          {(reference.business_types || []).map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Business Sector *</label>
        <select className="form-select" value={form.business_sector} onChange={(e) => update('business_sector', e.target.value)}>
          <option value="">Select...</option>
          {(reference.business_sectors || []).map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>
    </div>

    <div className="form-group">
      <label className="form-label">Business Location</label>
      <input type="text" className="form-input" value={form.business_location} onChange={(e) => update('business_location', e.target.value)} placeholder="e.g., Red Light Market, Paynesville" />
    </div>

    <div className="form-group">
      <label className="form-label">Tax Division</label>
      <select className="form-select" value={form.tax_division} onChange={(e) => update('tax_division', e.target.value)}>
        {(reference.tax_divisions || []).map((d) => (<option key={d.value} value={d.value}>{d.label}</option>))}
      </select>
    </div>

    <div className="form-group">
      <label className="form-label">Notes (optional)</label>
      <textarea className="form-textarea" value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Any additional information..." rows={3} />
    </div>
  </>
);

// =============================================
// STEP 5 — Preview
// =============================================
const PreviewStep = ({ preview, form, files }) => {
  const fileCount = Object.values(files).filter((f) => f !== null).length;
  return (
    <>
      <h2 className="section-title">Preview Generated Credentials</h2>

      {preview.has_warnings && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <FiAlertTriangle size={18} color="#92400e" />
            <strong style={{ color: '#92400e' }}>Duplicate warnings detected</strong>
          </div>
          {preview.duplicate_warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 13, color: '#78350f', marginBottom: 4 }}>• {w.message}</div>
          ))}
        </div>
      )}

      <div className="preview-grid">
        <PreviewField label="DSSN" value={preview.dssn_preview} highlight />
        <PreviewField label="TIN" value={preview.tin_preview} highlight />
        <PreviewField label="Business ID" value={preview.business_id_preview} />
        <PreviewField label="Temporary Password" value={preview.temporary_password} />
      </div>

      <h3 style={{ marginTop: 24, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Registration Summary</h3>

      <div className="preview-summary">
        <SummaryRow label="Full Name" value={`${form.first_name} ${form.middle_name || ''} ${form.last_name}`.replace(/\s+/g, ' ').trim()} />
        <SummaryRow label="Date of Birth" value={form.date_of_birth} />
        <SummaryRow label="Email" value={form.email} />
        <SummaryRow label="Phone" value={form.phone} />
        <SummaryRow label="Location" value={[form.community, form.township, form.district, form.county].filter(Boolean).join(', ') || '—'} />
        <SummaryRow label="Postal Code" value={form.postal_code || '—'} />
        <SummaryRow label="Business Name" value={form.business_name} />
        <SummaryRow label="Business Type" value={form.business_type} />
        <SummaryRow label="Tax Division" value={form.tax_division.replace(/_/g, ' ')} />
        <SummaryRow label="Documents attached" value={fileCount > 0 ? `${fileCount} file(s)` : 'None'} />
      </div>
    </>
  );
};

const PreviewField = ({ label, value, highlight }) => (
  <div style={{ background: highlight ? '#eff6ff' : '#f9fafb', border: `1px solid ${highlight ? '#93c5fd' : '#e5e7eb'}`, borderRadius: 8, padding: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'monospace', color: highlight ? '#1a73e8' : '#1f2937' }}>{value}</div>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}>
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

// =============================================
// STEP 6 — Success
// =============================================
const SuccessStep = ({ result, onPrint, onDownloadPDF, onAnother, onHistory }) => (
  <>
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
        <FiCheckCircle size={40} color="#16a34a" />
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Taxpayer Registered</h2>
      <p style={{ color: '#6b7280', marginTop: 4 }}>Give these credentials to the taxpayer</p>
    </div>

    <div id="credentials-card" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 20 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontFamily: 'monospace' }}>Registration: {result.registration_id}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, fontFamily: 'monospace' }}>Registered by: {result.registered_by.officer_id}</div>

      <CredentialRow label="Full Name" value={`${result.first_name} ${result.last_name}`} />
      <CredentialRow label="Business Name" value={result.business_name} />
      <div style={{ borderTop: '1px dashed #d1d5db', margin: '16px 0' }} />
      <CredentialRow label="DSSN (username)" value={result.dssn} mono highlight />
      <CredentialRow label="TIN" value={result.tin} mono highlight />
      <CredentialRow label="Business ID" value={result.business_id} mono />
      <div style={{ borderTop: '1px dashed #d1d5db', margin: '16px 0' }} />
      <CredentialRow label="Email (login)" value={result.email} mono />
      <CredentialRow label="Phone" value={result.phone} />
      <CredentialRow label="Temporary Password" value="12345" mono highlight />
    </div>

    <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#1e3a8a', marginBottom: 20 }}>
      <strong>Next steps for the taxpayer:</strong>
      <ul style={{ marginTop: 6, paddingLeft: 20 }}>
        <li>Log in at the Digital Liberia app using Email + DSSN + Password</li>
        <li>Change the temporary password immediately</li>
        <li>Use the app to manage inventory, file taxes, and access services</li>
      </ul>
    </div>

    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
      <button className="btn btn-secondary" onClick={onPrint}><FiPrinter size={18} />Print</button>
      <button className="btn btn-secondary" onClick={onDownloadPDF}><FiDownload size={18} />Download PDF</button>
      <button className="btn btn-secondary" onClick={onHistory}><FiClipboard size={18} />History</button>
      <button className="btn btn-primary" onClick={onAnother} style={{ background: '#16a34a', borderColor: '#16a34a' }}>
        <FiUserPlus size={18} />Register Another
      </button>
    </div>
  </>
);

const CredentialRow = ({ label, value, mono, highlight }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
    <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
    <span style={{ fontFamily: mono ? 'monospace' : 'inherit', fontWeight: 600, fontSize: mono ? 16 : 14, color: highlight ? '#1a73e8' : '#1f2937' }}>{value}</span>
  </div>
);

export default NewRegistration;
